import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, X, User, FloppyDisk, ArrowLeft, ArrowRight, Sparkle, Robot, SmileyWink, Warning, ShieldCheck, Printer, Lightning } from "@phosphor-icons/react";
import {
  listPlanners, savePlanner, getPlanner, deletePlanner, getPlannerSummary,
  getDriProfiles, listRecipes, autoGeneratePlan, getKidsConfig, getKidSnackIdeas,
  NUTRIENT_LABELS, NUTRIENT_UNITS, MACRO_KEYS,
} from "../lib/api";
import { ALLERGY_PRESETS } from "../lib/allergens";
import NutrientView from "../components/NutrientView";

const SLOTS = ["breakfast", "lunch", "dinner", "snack"];

function mondayOf(iso) {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}
function fmt(v) {
  const n = Number(v || 0);
  return Math.abs(n) >= 100 ? n.toFixed(0) : n.toFixed(1);
}

export default function MealPlanner() {
  const [plans, setPlans] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [current, setCurrent] = useState(null); // full plan object
  const [summary, setSummary] = useState(null);
  const [activePersonId, setActivePersonId] = useState(null);
  const [pickerCell, setPickerCell] = useState(null); // {date, slot}
  const [kidsConfig, setKidsConfig] = useState(null);
  const [kidsModeManual, setKidsModeManual] = useState(null); // null = auto; true/false override

  const loadPlans = useCallback(async () => {
    const d = await listPlanners(); setPlans(d.plans || []);
  }, []);
  useEffect(() => {
    (async () => {
      const [pr, r, kc] = await Promise.all([getDriProfiles(), listRecipes(), getKidsConfig()]);
      setProfiles(pr.profiles || []); setRecipes(r.recipes || []); setKidsConfig(kc);
      await loadPlans();
    })();
  }, [loadPlans]);

  const loadSummary = useCallback(async (id) => {
    try { const s = await getPlannerSummary(id); setSummary(s); }
    catch (e) { /* noop */ }
  }, []);

  const openPlan = async (id) => {
    const p = await getPlanner(id);
    setCurrent(p);
    setActivePersonId(p.people[0]?.id || null);
    loadSummary(id);
  };

  const dates = useMemo(() => {
    if (!current) return [];
    const start = new Date(current.start_date);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
  }, [current]);

  const createBlank = () => {
    const start = mondayOf(new Date().toISOString().slice(0, 10));
    const person = { id: crypto.randomUUID(), name: "Person 1", profile: "adult_male_19_30" };
    setCurrent({ id: null, name: "New Plan", start_date: start, people: [person], meals: {} });
    setActivePersonId(person.id);
    setSummary(null);
  };

  const [autoLoading, setAutoLoading] = useState(false);
  const autoGenerate = async () => {
    // Base people on current plan if any, else prompt for a default family
    let people = current?.people;
    if (!people || people.length === 0) {
      const p1 = { id: crypto.randomUUID(), name: "Adult 1", profile: "adult_male_31_50" };
      const p2 = { id: crypto.randomUUID(), name: "Adult 2", profile: "adult_female_31_50" };
      people = [p1, p2];
    }
    const start = current?.start_date || mondayOf(new Date().toISOString().slice(0, 10));
    setAutoLoading(true);
    try {
      const plan = await autoGeneratePlan({
        name: current?.name || "Auto Family Plan",
        start_date: start,
        people,
      });
      toast.success("Plan auto-generated");
      setCurrent(plan);
      setActivePersonId(plan.people[0]?.id);
      loadSummary(plan.id); loadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-plan failed — save at least a couple of recipes first");
    } finally { setAutoLoading(false); }
  };

  const addPerson = () => {
    if (!current) return;
    const p = { id: crypto.randomUUID(), name: `Person ${current.people.length + 1}`,
                profile: "adult_female_19_30", allergies: [], dislikes: [] };
    setCurrent(prev => ({ ...prev, people: [...prev.people, p] }));
  };
  const removePerson = (pid) => {
    setCurrent(prev => ({ ...prev, people: prev.people.filter(p => p.id !== pid) }));
    if (activePersonId === pid) setActivePersonId(current.people[0]?.id);
  };
  const updPerson = (pid, key, val) => {
    setCurrent(prev => ({ ...prev, people: prev.people.map(p => p.id === pid ? { ...p, [key]: val } : p) }));
  };
  const updPersonList = (pid, key, csv) => {
    const list = csv.split(",").map(s => s.trim()).filter(Boolean);
    updPerson(pid, key, list);
  };

  const isPresetActive = (person, preset) =>
    preset.ingredients.every(i => (person.allergies || []).includes(i));

  const togglePreset = (pid, preset) => {
    const person = current.people.find(p => p.id === pid);
    if (!person) return;
    const cur = new Set(person.allergies || []);
    const active = isPresetActive(person, preset);
    preset.ingredients.forEach(i => active ? cur.delete(i) : cur.add(i));
    updPerson(pid, "allergies", Array.from(cur));
  };

  const assignRecipe = (date, slot, recipeId) => {
    if (!current) return;
    const servings = {};
    for (const p of current.people) servings[p.id] = 1;
    setCurrent(prev => {
      const meals = { ...(prev.meals || {}) };
      meals[date] = { ...(meals[date] || {}) };
      meals[date][slot] = { recipe_id: recipeId, servings };
      return { ...prev, meals };
    });
    setPickerCell(null);
  };
  const clearSlot = (date, slot) => {
    setCurrent(prev => {
      const meals = { ...(prev.meals || {}) };
      if (meals[date]) { meals[date] = { ...meals[date] }; delete meals[date][slot]; }
      return { ...prev, meals };
    });
  };
  const updServings = (date, slot, pid, srv) => {
    setCurrent(prev => {
      const meals = { ...(prev.meals || {}) };
      meals[date] = { ...(meals[date] || {}) };
      meals[date][slot] = { ...(meals[date][slot] || {}) };
      meals[date][slot].servings = { ...(meals[date][slot].servings || {}), [pid]: Number(srv) || 0 };
      return { ...prev, meals };
    });
  };

  const save = async () => {
    if (!current) return;
    try {
      const saved = await savePlanner({
        id: current.id, name: current.name, start_date: current.start_date,
        people: current.people, meals: current.meals || {},
      });
      setCurrent(saved);
      toast.success("Saved");
      loadPlans(); loadSummary(saved.id);
    } catch (e) { toast.error("Save failed"); }
  };

  const removePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    await deletePlanner(id); toast.success("Deleted");
    setCurrent(null); loadPlans();
  };

  const recipeById = (id) => recipes.find(r => r.id === id);
  const activePerson = current?.people.find(p => p.id === activePersonId);
  const activeStats = activePerson && summary?.per_person?.[activePerson.id];

  // ---- Snack Ideas state ----
  const [snackDate, setSnackDate] = useState("");
  const [snackData, setSnackData] = useState(null);
  const [snackLoading, setSnackLoading] = useState(false);
  const [snackTargetSlot, setSnackTargetSlot] = useState("snack");

  const fetchSnacks = async (date) => {
    if (!current?.id || !activePersonId || !date) {
      toast.error("Save the plan first, then pick a day");
      return;
    }
    setSnackLoading(true); setSnackDate(date); setSnackData(null);
    try {
      const d = await getKidSnackIdeas(current.id, activePersonId, date, 5);
      setSnackData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch snacks");
    } finally { setSnackLoading(false); }
  };

  const addSnackAsRecipe = async (snack) => {
    // Wrap snack food as a simple 1-ingredient recipe and add to the plan.
    try {
      const built = await (await import("../lib/api")).saveBuilderRecipe(
        `${snack.emoji} ${snack.food}`, 1,
        [{ name: snack.food, grams: snack.grams }]
      );
      // reload recipes then assign
      const r = await listRecipes(); setRecipes(r.recipes || []);
      assignRecipe(snackDate, snackTargetSlot, built.id);
      toast.success(`Added ${snack.food} to ${snackTargetSlot}`);
    } catch (e) { toast.error("Could not add snack"); }
  };

  // ---- Kids Mode ----
  const kidKeys = kidsConfig?.kid_profile_keys || [];
  const isKidProfile = (key) => kidKeys.includes(key);
  const autoKidsMode = !!(activePerson && isKidProfile(activePerson.profile));
  const kidsMode = kidsModeManual == null ? autoKidsMode : kidsModeManual;
  const activeAgeBand = activeStats?.age_band || kidsConfig?.kid_age_band?.[activePerson?.profile];
  const activeKidHighlights = kidsConfig?.highlight_nutrients || [];

  const emojiFor = (name) => {
    if (!kidsConfig?.food_emoji || !name) return "";
    const low = name.toLowerCase();
    if (kidsConfig.food_emoji[low]) return kidsConfig.food_emoji[low];
    for (const [k, v] of Object.entries(kidsConfig.food_emoji)) {
      if (low.includes(k) || k.includes(low)) return v;
    }
    return "🍽️";
  };
  const recipeEmoji = (r) => {
    if (!r) return "🍽️";
    const names = (r.ingredients || []).map(i => (i.name || "").toLowerCase());
    // pick emoji from first curated match with meaningful weight
    const emap = kidsConfig?.food_emoji || {};
    for (const n of names) {
      if (emap[n]) return emap[n];
      for (const [k, v] of Object.entries(emap)) {
        if (n.includes(k) || k.includes(n)) return v;
      }
    }
    return "🍽️";
  };
  const safetyForRecipe = (rid) => {
    if (!kidsMode || !activeAgeBand) return null;
    return summary?.kid_safety?.[activeAgeBand]?.[rid] || null;
  };
  const toneClass = (tone) => (
    tone === "danger" ? "bg-red-100 text-red-700 border-red-300"
    : tone === "warn" ? "bg-amber-100 text-amber-700 border-amber-300"
    : "bg-emerald-100 text-emerald-700 border-emerald-300"
  );

  return (
    <div className={`mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6 ${kidsMode ? "kids-mode" : ""}`} data-testid="planner-page" data-kids-mode={kidsMode ? "on" : "off"}>
      <div className="pb-6 border-b border-border flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Weekly meal planner</div>
          <h1 className={`font-display font-black text-4xl md:text-5xl tracking-tighter mt-2 ${kidsMode ? "text-pink-600" : ""}`}>
            {kidsMode ? "🎨 Kids Planner" : "Planner"}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            {kidsMode
              ? "Playful, kid-focused view — highlights child DRIs and flags choking hazards, high sugar & sodium per age band."
              : "Plan 7 days across up to 4 slots per day. Add multiple people with different DRI profiles — see each person's coverage & deficits."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setKidsModeManual(kidsMode ? false : true)}
            data-testid="kids-mode-toggle"
            title={autoKidsMode && kidsModeManual == null ? "Auto-on (kid persona selected). Click to override." : "Toggle Kids Mode"}
            className={`h-10 px-4 border font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-2 transition-colors duration-200 ${
              kidsMode
                ? "bg-pink-500 text-white border-pink-500 hover:bg-pink-600"
                : "border-border hover:bg-muted"
            }`}>
            <SmileyWink size={14} weight={kidsMode ? "fill" : "regular"} />
            Kids Mode {kidsMode ? "ON" : "OFF"}
          </button>
          <button onClick={autoGenerate} data-testid="auto-plan-btn"
            className="h-10 px-4 border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-2 transition-colors duration-200">
            <Robot size={14} /> Auto-plan week
          </button>
          <button onClick={createBlank} data-testid="new-plan-btn"
            className="h-10 px-4 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90 flex items-center gap-2 transition-colors duration-200">
            <Plus size={14} /> New plan
          </button>
        </div>
      </div>

      {!current && (
        plans.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center" data-testid="empty-plans">
            <div className="font-display font-bold text-lg">No plans yet</div>
            <div className="text-muted-foreground text-sm mt-1 mb-6">Create your first weekly plan.</div>
            <button onClick={createBlank} className="h-11 px-5 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90">Create plan</button>
          </div>
        ) : (
          <div className="border border-border" data-testid="plans-list">
            {plans.map(p => (
              <div key={p.id} className="grid grid-cols-[1fr_120px_100px_60px] gap-4 items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors duration-150" data-testid={`plan-${p.id}`}>
                <button onClick={() => openPlan(p.id)} className="text-left" data-testid={`open-plan-${p.id}`}>
                  <div className="font-medium">{p.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">Week of {p.start_date}</div>
                </button>
                <div className="font-mono text-xs text-muted-foreground text-right">{p.people?.length || 0} people</div>
                <div className="font-mono text-xs text-muted-foreground text-right">{Object.keys(p.meals || {}).length} days</div>
                <button onClick={() => removePlan(p.id)} data-testid={`del-plan-${p.id}`}
                  className="h-8 w-8 border border-border hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center transition-colors duration-200 justify-self-end">
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {current && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-px bg-border border border-border" data-testid="plan-header">
            <div className="bg-card p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Plan name</div>
              <input value={current.name} onChange={e => setCurrent(prev => ({ ...prev, name: e.target.value }))}
                data-testid="plan-name"
                className="w-full h-9 px-2 border border-border bg-background focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
            <div className="bg-card p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Week of</div>
              <input type="date" value={current.start_date}
                onChange={e => setCurrent(prev => ({ ...prev, start_date: e.target.value }))}
                data-testid="plan-start"
                className="h-9 px-3 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
            <div className="bg-card p-3 flex items-end gap-2">
              <button onClick={() => setCurrent(null)} data-testid="close-plan"
                className="h-9 px-3 border border-border hover:bg-muted font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">Close</button>
              {kidsMode && current?.id && (
                <button onClick={async () => {
                  try { await save(); } catch { /* toast already fired */ }
                  window.open(`/planner/${current.id}/kid-card?person=${activePersonId || ""}`, "_blank");
                }}
                  data-testid="share-card-btn"
                  className="h-9 px-3 border border-pink-500 text-pink-600 hover:bg-pink-500 hover:text-white font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200">
                  <Printer size={13} /> Share card
                </button>
              )}
              <button onClick={save} data-testid="save-plan"
                className="h-9 px-4 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90 flex items-center gap-1.5 transition-colors duration-200">
                <FloppyDisk size={14} /> Save
              </button>
            </div>
          </div>

          {/* People */}
          <div className="border border-border" data-testid="people-section">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">People ({current.people.length})</div>
              <button onClick={addPerson} data-testid="add-person"
                className="h-8 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
                <Plus size={12} /> Add person
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
              {current.people.map(p => (
                <div key={p.id} className={`bg-card p-3 space-y-2 ${activePersonId === p.id ? "ring-2 ring-primary ring-inset" : ""}`} data-testid={`person-${p.id}`}>
                  <div className="grid grid-cols-[auto_1fr_1fr_28px] gap-2 items-center">
                    <button onClick={() => setActivePersonId(p.id)} className="h-8 w-8 border border-border flex items-center justify-center" data-testid={`select-person-${p.id}`}>
                      <User size={14} weight={activePersonId === p.id ? "fill" : "regular"} className={activePersonId === p.id ? "text-primary" : ""} />
                    </button>
                    <input value={p.name} onChange={e => updPerson(p.id, "name", e.target.value)}
                      data-testid={`person-name-${p.id}`}
                      className="h-8 px-2 border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
                    <select value={p.profile} onChange={e => updPerson(p.id, "profile", e.target.value)}
                      data-testid={`person-profile-${p.id}`}
                      className="h-8 px-2 border border-border bg-background font-mono text-xs focus:outline-none focus:border-primary transition-colors duration-200">
                      {profiles.map(pr => <option key={pr.key} value={pr.key}>{pr.label}</option>)}
                    </select>
                    <button onClick={() => removePerson(p.id)} disabled={current.people.length === 1}
                      className="h-8 w-7 border border-border hover:bg-muted disabled:opacity-40 flex items-center justify-center transition-colors duration-200"><X size={11} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Allergies</div>
                      <input value={(p.allergies || []).join(", ")}
                        onChange={e => updPersonList(p.id, "allergies", e.target.value)}
                        data-testid={`person-allergies-${p.id}`}
                        placeholder="e.g. peanut, egg"
                        className="w-full h-7 px-2 border border-primary/40 bg-background text-xs focus:outline-none focus:border-primary transition-colors duration-200" />
                    </div>
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Dislikes</div>
                      <input value={(p.dislikes || []).join(", ")}
                        onChange={e => updPersonList(p.id, "dislikes", e.target.value)}
                        data-testid={`person-dislikes-${p.id}`}
                        placeholder="e.g. mushroom, olives"
                        className="w-full h-7 px-2 border border-border bg-background text-xs focus:outline-none focus:border-primary transition-colors duration-200" />
                    </div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground mb-1">One-tap allergy presets</div>
                    <div className="flex flex-wrap gap-1" data-testid={`allergy-presets-${p.id}`}>
                      {ALLERGY_PRESETS.map(preset => {
                        const active = isPresetActive(p, preset);
                        return (
                          <button key={preset.key} onClick={() => togglePreset(p.id, preset)}
                            data-testid={`preset-${p.id}-${preset.key}`}
                            className={`h-6 px-2 border font-mono text-[10px] uppercase tracking-[0.1em] transition-colors duration-200 ${
                              active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
                            }`}>{preset.label}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div className="border border-border overflow-x-auto" data-testid="plan-grid">
            <table className="w-full border-collapse text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground w-24">Slot</th>
                  {dates.map(d => (
                    <th key={d} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{d.slice(5)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SLOTS.map(slot => (
                  <tr key={slot} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground align-top pt-3">{slot}</td>
                    {dates.map(d => {
                      const meal = current.meals?.[d]?.[slot];
                      const r = meal && recipeById(meal.recipe_id);
                      const safety = r && safetyForRecipe(r.id);
                      return (
                        <td key={d} className="p-1.5 align-top border-l border-border" data-testid={`cell-${d}-${slot}`}>
                          {r ? (
                            <div className={`bg-card p-2 space-y-1 border ${kidsMode ? "rounded-2xl border-pink-200 shadow-sm bg-gradient-to-br from-pink-50/50 to-amber-50/40" : "border-border"}`}>
                              <div className="flex items-start justify-between gap-1">
                                <Link to={`/recipe/${r.id}`} className={`font-medium leading-tight truncate hover:underline flex-1 flex items-center gap-1 ${kidsMode ? "text-sm" : "text-xs"}`}>
                                  {kidsMode && <span className="text-base" data-testid={`emoji-${r.id}`}>{recipeEmoji(r)}</span>}
                                  <span className="truncate">{r.name}</span>
                                </Link>
                                <button onClick={() => clearSlot(d, slot)} className={`h-5 w-5 border flex items-center justify-center shrink-0 ${kidsMode ? "rounded-full border-pink-300 hover:bg-pink-100" : "border-border hover:bg-muted"}`}><X size={10} /></button>
                              </div>
                              <div className="font-mono text-[9px] text-muted-foreground">{Math.round(r.per_serving?.calories || 0)} kcal/srv</div>
                              {kidsMode && safety && (
                                <div className="flex flex-wrap gap-1" data-testid={`safety-${d}-${slot}`}>
                                  {safety.badges.map(b => (
                                    <span key={b.key} className={`px-1.5 py-0.5 border rounded-full font-mono text-[9px] uppercase tracking-wide ${toneClass(b.tone)}`} data-testid={`badge-${b.key}-${r.id}`}>
                                      {b.tone === "danger" && <Warning size={9} className="inline mr-0.5" weight="fill" />}
                                      {b.tone === "good" && <ShieldCheck size={9} className="inline mr-0.5" weight="fill" />}
                                      {b.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {activePerson && (
                                <div className="flex items-center gap-1">
                                  <span className="font-mono text-[9px] text-muted-foreground">{activePerson.name}:</span>
                                  <input type="number" min="0" step="0.25" value={meal.servings?.[activePerson.id] ?? 0}
                                    onChange={e => updServings(d, slot, activePerson.id, e.target.value)}
                                    data-testid={`srv-${d}-${slot}-${activePerson.id}`}
                                    className={`w-12 h-6 px-1 border bg-background font-mono text-[10px] text-right focus:outline-none focus:border-primary transition-colors duration-200 ${kidsMode ? "rounded-md border-pink-300" : "border-border"}`} />
                                  <span className="font-mono text-[9px] text-muted-foreground">srv</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <button onClick={() => setPickerCell({ date: d, slot })} data-testid={`add-${d}-${slot}`}
                              className={`w-full min-h-[54px] border border-dashed flex items-center justify-center text-muted-foreground transition-colors duration-200 ${kidsMode ? "rounded-2xl border-pink-200 hover:border-pink-500 hover:bg-pink-50/60" : "border-border hover:border-primary hover:bg-muted/40"}`}>
                              <Plus size={14} />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Picker modal */}
          {pickerCell && (
            <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPickerCell(null)} data-testid="picker-modal">
              <div className="bg-card border border-border w-full max-w-lg max-h-[70vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pick recipe — {pickerCell.date} · {pickerCell.slot}</div>
                  <button onClick={() => setPickerCell(null)} className="h-8 w-8 border border-border hover:bg-muted flex items-center justify-center"><X size={12} /></button>
                </div>
                <div className="overflow-y-auto">
                  {recipes.length === 0 && <div className="p-4 text-sm text-muted-foreground">No recipes yet.</div>}
                  {recipes.map(r => {
                    const rSafety = safetyForRecipe(r.id);
                    return (
                      <button key={r.id} onClick={() => assignRecipe(pickerCell.date, pickerCell.slot, r.id)}
                        data-testid={`picker-${r.id}`}
                        className="w-full text-left grid grid-cols-[1fr_100px] gap-3 px-4 py-2 border-b border-border last:border-b-0 hover:bg-muted transition-colors duration-200">
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {kidsMode && <span className="text-lg">{recipeEmoji(r)}</span>}
                            {r.name}
                            {kidsMode && rSafety && rSafety.badges.slice(0,1).map(b => (
                              <span key={b.key} className={`px-1.5 py-0.5 border rounded-full font-mono text-[9px] uppercase ${toneClass(b.tone)}`}>{b.label}</span>
                            ))}
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground">{r.ingredients?.length || 0} ings · {(r.diet_tags || []).slice(0,3).join(" · ")}</div>
                        </div>
                        <div className="text-right font-mono text-sm">{Math.round(r.per_serving?.calories || 0)} kcal</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Per-person summary */}
          {activeStats && (
            <div data-testid="active-summary">
              <h2 className={`font-display font-bold text-2xl tracking-tight mb-3 ${kidsMode ? "text-pink-600 flex items-center gap-2" : ""}`}>
                {kidsMode && <SmileyWink size={22} weight="fill" />}
                {activePerson.name}'s {kidsMode ? "kid coverage" : "coverage"}
                <span className={`font-mono text-sm font-normal ${kidsMode ? "text-pink-500/80" : "text-muted-foreground"}`}> · {activeStats.dri.label} · {activeStats.logged_days} planned day{activeStats.logged_days === 1 ? "" : "s"}</span>
              </h2>
              {kidsMode && activeStats.is_kid === false && (
                <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="kids-mode-nonkid-note">
                  Playful view is on, but this person's profile isn't a kid persona — switch profile to a Child/Teen band for kid DRIs.
                </div>
              )}
              <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border border border-border ${kidsMode ? "rounded-2xl overflow-hidden" : ""}`}>
                {(kidsMode ? activeKidHighlights : ["calories","protein","carbs","fat","fiber","iron","calcium","vitamin_c"]).map(k => {
                  const val = activeStats.avg_daily[k] || 0;
                  const tgt = activeStats.dri.values[k] || 0;
                  const pct = Math.min(200, activeStats.avg_pcts[k] || 0);
                  const emoji = { protein: "💪", calcium: "🦴", iron: "🩸", vitamin_d: "☀️", fiber: "🌾", zinc: "🛡️", vitamin_c: "🍊", vitamin_a: "🥕" }[k];
                  return (
                    <div key={k} className={`bg-card p-4 ${kidsMode ? "hover:bg-pink-50/40 transition-colors" : ""}`} data-testid={`stat-${k}`}>
                      <div className="flex items-baseline justify-between">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${kidsMode ? "text-pink-700 font-semibold" : "text-muted-foreground"}`}>
                          {kidsMode && emoji && <span className="mr-1 text-base align-middle">{emoji}</span>}
                          {NUTRIENT_LABELS[k]}
                        </span>
                        <span className="font-mono text-xs font-medium">{pct}%</span>
                      </div>
                      <div className="mt-1 font-mono text-lg">{fmt(val)} <span className="text-xs text-muted-foreground">/ {fmt(tgt)} {NUTRIENT_UNITS[k]}</span></div>
                      <div className={`h-1.5 mt-2 overflow-hidden ${kidsMode ? "bg-pink-100 rounded-full" : "bg-muted"}`}>
                        <div className={`h-full transition-all duration-500 ${kidsMode ? "bg-gradient-to-r from-pink-400 to-amber-400 rounded-full" : "bg-primary"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Full 44-nutrient dashboard for the active person */}
              <div className="mt-4">
                <NutrientView
                  nutrients={activeStats.avg_daily}
                  dri={activeStats.dri.values}
                  defaultTab="standard"
                  defaultView="dashboard"
                  testIdPrefix="planner-nutrient-view"
                />
              </div>
            </div>
          )}

          {/* Kid Snack Ideas — visible in Kids Mode when a kid person is active + plan saved */}
          {kidsMode && activeStats?.is_kid && current?.id && (
            <div className="border-2 border-pink-300 rounded-2xl bg-gradient-to-br from-pink-50 to-amber-50 p-5 space-y-3" data-testid="kid-snack-ideas">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Lightning size={22} weight="fill" className="text-pink-500" />
                  <h3 className="font-display font-bold text-xl text-pink-700">Snack ideas for {activePerson.name}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <select value={snackDate} onChange={e => setSnackDate(e.target.value)}
                    data-testid="snack-date-select"
                    className="h-9 px-2 rounded-lg border border-pink-300 bg-white font-mono text-xs focus:outline-none focus:border-pink-500">
                    <option value="">Pick a day…</option>
                    {dates.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={snackTargetSlot} onChange={e => setSnackTargetSlot(e.target.value)}
                    data-testid="snack-slot-select"
                    className="h-9 px-2 rounded-lg border border-pink-300 bg-white font-mono text-xs focus:outline-none focus:border-pink-500">
                    {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => fetchSnacks(snackDate)} disabled={!snackDate || snackLoading}
                    data-testid="fetch-snacks-btn"
                    className="h-9 px-4 bg-pink-500 text-white rounded-lg font-mono text-xs uppercase tracking-[0.15em] hover:bg-pink-600 disabled:opacity-50 flex items-center gap-1.5 transition-colors duration-200">
                    {snackLoading ? "Loading…" : "Suggest"}
                  </button>
                </div>
              </div>

              {!snackData && !snackLoading && (
                <p className="text-sm text-pink-700/70">Save your plan, pick a day, then get one-tap snack ideas that fill this child's biggest missing kid-nutrient — filtered for their age band's choking hazards and allergies.</p>
              )}

              {snackData?.top_deficit && (
                <div className="bg-white/70 border border-pink-200 rounded-xl p-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-pink-600 mb-1">Biggest gap today</div>
                  <div className="text-lg font-display font-bold text-pink-800">
                    {snackData.top_deficit.label} — {snackData.top_deficit.current} / {snackData.top_deficit.target} {snackData.top_deficit.unit}
                    <span className="ml-2 font-mono text-sm font-normal text-pink-600/80">({snackData.top_deficit.pct_covered}% covered · {snackData.top_deficit.gap} {snackData.top_deficit.unit} short)</span>
                  </div>
                </div>
              )}

              {snackData?.snacks?.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="snack-list">
                  {snackData.snacks.map(s => (
                    <button key={s.food} onClick={() => addSnackAsRecipe(s)}
                      data-testid={`snack-${s.food.replace(/\s+/g, "-")}`}
                      className="text-left bg-white border-2 border-pink-200 hover:border-pink-500 hover:shadow-md rounded-2xl p-4 transition-all duration-200 group">
                      <div className="flex items-start gap-3">
                        <div className="text-4xl">{s.emoji}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold text-base capitalize truncate">{s.food}</div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-pink-600 mt-0.5">{s.grams} g portion</div>
                          <div className="text-xs text-muted-foreground mt-1">{s.reason}</div>
                          {s.already_in_day && <div className="font-mono text-[9px] mt-1 text-amber-600 uppercase tracking-wider">Already used today</div>}
                          <div className="mt-2 font-mono text-[10px] text-pink-700 uppercase tracking-[0.15em] opacity-0 group-hover:opacity-100 transition-opacity">Tap to add →</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {snackData && snackData.snacks?.length === 0 && (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  {snackData.note || "No safe snack matches found — either the day already meets kid-nutrient targets or every candidate hit an allergy/hazard."}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
